import React, { useCallback, useMemo } from 'react';
import { useStore, getBezierPath, Position } from 'reactflow';

/**
 * Computes the intersection point on a node's bounding rectangle
 * along the line from that node's center toward another node's center.
 */
function getIntersection(nodePos, nodeW, nodeH, otherPos, otherW, otherH) {
  const cx = nodePos.x + nodeW / 2;
  const cy = nodePos.y + nodeH / 2;
  const ox = otherPos.x + otherW / 2;
  const oy = otherPos.y + otherH / 2;

  const dx = ox - cx;
  const dy = oy - cy;

  if (dx === 0 && dy === 0) return { x: cx, y: cy, position: Position.Top };

  const hw = nodeW / 2;
  const hh = nodeH / 2;

  // Scale factor to reach the rectangle edge
  const sx = hw / Math.abs(dx || 1);
  const sy = hh / Math.abs(dy || 1);
  const s = Math.min(sx, sy);

  const x = cx + dx * s;
  const y = cy + dy * s;

  // Determine which side we hit
  let position;
  if (s === sx) {
    position = dx > 0 ? Position.Right : Position.Left;
  } else {
    position = dy > 0 ? Position.Bottom : Position.Top;
  }

  return { x, y, position };
}

const FloatingLink = ({ id, source, target, style, markerEnd }) => {
  const sourceNode = useStore(useCallback(store => store.nodeInternals.get(source), [source]));
  const targetNode = useStore(useCallback(store => store.nodeInternals.get(target), [target]));

  if (!sourceNode || !targetNode) return null;

  const sw = sourceNode.width || 250;
  const sh = sourceNode.height || 54;
  const tw = targetNode.width || 250;
  const th = targetNode.height || 54;

  const sourceInt = getIntersection(sourceNode.position, sw, sh, targetNode.position, tw, th);
  const targetInt = getIntersection(targetNode.position, tw, th, sourceNode.position, sw, sh);

  const edgePath = getBezierPath({
    sourceX: sourceInt.x,
    sourceY: sourceInt.y,
    sourcePosition: sourceInt.position,
    targetX: targetInt.x,
    targetY: targetInt.y,
    targetPosition: targetInt.position,
  });

  if (!edgePath || edgePath.length <= 0) return null;

  // Normalize legacy styles: old DB edges had strokeOpacity:'10%' making them invisible
  const normalizedStyle = useMemo(() => {
    if (!style) return style;
    const opacity = style.strokeOpacity;
    if (opacity === '10%' || opacity === 0.1) {
      const { strokeOpacity, test, ...rest } = style;
      return { ...rest, strokeOpacity: 0.5 };
    }
    return style;
  }, [style]);

  return (
    <path
      id={id}
      className="react-flow__edge-path"
      d={edgePath[0]}
      markerEnd={markerEnd}
      style={normalizedStyle}
    />
  );
};

export default FloatingLink;
